import { FormattedMessage, useIntl } from 'react-intl';
import { DataResult } from 'models/data';
import type { FlagCountryCode } from 'components/Flag';
import { getChartColorWithMax } from 'components/chart/data/ChartColors';
import { getLabelForDatapoint } from 'pages/insights-pro/graphs/graphUtils';
import { Flag } from 'components/Flag';
import { MeasureComparison } from 'components/MeasureComparison';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { Service } from 'services/data.service';
import { Tile } from 'components/Layout/PageContainer';
import { OverTimeCommonData, OverTimeReport } from 'pages/insights-pro/common/reports/over-time-chart/OverTimeReport';
import { useModelService } from 'services/model/model.service';
import { OverTimeReportInfo } from 'pages/insights-pro/common/reports/over-time-chart/models';
import { OverTimeReportMetrics } from 'pages/insights-pro/common/reports/over-time-chart/OverTimeReportMetrics';
import { useEffect } from 'react';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { OverTimeChart } from '../../../common/reports/over-time-chart/OverTimeChart';
import { CountriesOverTimeReportInfoContent } from './CountriesOverTimeReportInfoContent';

export function CountriesOverTimeReport() {
  const intl = useIntl();
  const { setContent } = useInfoDrawer();

  const { getMeasureByMetric, getAttribute } = useModelService();

  const chnCountry = getAttribute('chnCountry');

  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');

  useEffect(() => {
    const infoContent = {
      titleKey: 'insights.country.reports.over_time',
      subtitleKey: 'info.content.all_dates_utc',
      content: <CountriesOverTimeReportInfoContent />,
    };

    setContent(infoContent);

    return () => {
      setContent(null);
    };
  }, [setContent, ordGmvVal]);

  const extractCountryInfo = (data: DataResult) => {
    const countries = data.sets.countries.map(entry => {
      const countryName = chnCountry.getValue(entry.attributes);
      return {
        code: chnCountry.getRawValue(entry.attributes).toLowerCase().trim(),
        name: countryName,
        totalGmv: ordGmvVal.getValue(entry.values),
        compareGmv: ordGmvVal.getValue(entry.compareValues),
        color: '',
        isOther: false,
        timeInfos: entry.sets.timeSeries.map(timeEntry => ({
          code: getLabelForDatapoint(timeEntry, intl),
          gmv: ordGmvVal.getValue(timeEntry.values),
          comparisonGmv: ordGmvVal.getValue(timeEntry.compareValues),
        })),
        rank: 0,
        compareRank: 0,
      };
    });

    const sortedByCurrent = [...countries]
      .sort((a, b) => b.totalGmv - a.totalGmv)
      .map((country, idx) => ({ ...country, rank: idx + 1 }));

    const sortedByCompare = [...sortedByCurrent]
      .sort((a, b) => b.compareGmv - a.compareGmv)
      .map((country, idx) => ({ ...country, compareRank: idx + 1 }));

    const finalSorted = [...sortedByCompare].sort((a, b) => b.totalGmv - a.totalGmv);

    return finalSorted.map((country, index) => ({
      ...country,
      color: getChartColorWithMax(index, 5),
      isOther: index >= 5,
      rank: index + 1,
    }));
  };

  const extractTotals = (data: DataResult) => ({
    totalGmv: ordGmvVal.getValue(data.values),
    compareGmv: ordGmvVal.getValue(data.compareValues),
  });

  const renderCountryRow = (country: CountryOverTimeReportInfo, commonData: OverTimeCommonData) => {
    const currentVal = country.totalGmv ?? 0;
    const compareVal = country.compareGmv ?? 0;

    const { marketShare, compareShare, growth } = OverTimeReportMetrics(
      currentVal,
      compareVal,
      commonData.totalGmvAllItems,
      commonData.totalCompareGmvAllItems
    );

    return (
      <div key={country.code} className="w-full bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Flag countryCode={country.code} tooltip={country.name} />
          <p className="text-lg font-bold">{country.name}</p>
        </div>

        <div className="grid grid-cols-4 divide-x divide-gray-200 p-4">
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">{ordGmvVal.metric.name}</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">
                {Intl.NumberFormat('en', {
                  notation: 'compact',
                  maximumFractionDigits: 2,
                }).format(currentVal)}
              </span>
              {commonData.isComparisonActive && compareVal !== undefined && (
                <MeasureComparison
                  value={currentVal}
                  comparisonValue={compareVal}
                  unit={MeasureUnitCode.QUANTITY}
                  interpretation={1}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">
              <FormattedMessage id="insights.report.market_share" />
            </span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">{marketShare}%</span>
              {commonData.isComparisonActive && (
                <MeasureComparison
                  value={parseFloat(marketShare)}
                  comparisonValue={parseFloat(compareShare)}
                  unit={MeasureUnitCode.QUANTITY}
                  interpretation={1}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">
              <FormattedMessage id="insights.report.growth" />
            </span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">{growth}%</span>
            </div>
          </div>
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">
              <FormattedMessage id="insights.report.country_ranking" />
            </span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">#{country.rank}</span>
              {commonData.isComparisonActive && (
                <MeasureComparison
                  value={country.compareRank}
                  comparisonValue={country.rank}
                  unit={MeasureUnitCode.COUNT}
                  interpretation={1}
                />
              )}
            </div>
          </div>
        </div>
        <Tile
          title={ordGmvVal.metric.name}
          additionalTitle={intl.formatMessage({
            id: 'general.title.over_time',
          })}
        >
          <OverTimeChart overTimeReport={country} timeInfos={commonData.timeInfos} measure={ordGmvVal} />
        </Tile>
      </div>
    );
  };

  return (
    <OverTimeReport
      service={Service.Countries}
      testId="countries-over-time-chart"
      extractItems={extractCountryInfo}
      extractTotals={extractTotals}
      titleId="insights.country.reports.over_time"
      descriptionId="insights.country.reports.over_time.description"
      additionalTitle={intl.formatMessage(
        {
          id: 'general.title.by-metric',
        },
        {
          metric: ordGmvVal.metric.name,
        }
      )}
      renderRow={renderCountryRow}
    />
  );
}

interface CountryOverTimeReportInfo extends OverTimeReportInfo {
  code: FlagCountryCode;
}
